import { z } from 'zod';

import { DataState } from './common.js';
import { METRIC_KEYS, metricKeySchema, type HierarchyQuery, type MetricKey } from './analytics.js';

/**
 * ADIM 4 — KAMPANYA EKRANLARININ SÖZLEŞMESİ (okuma tarafı).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu dosya YENİ bir uç sözleşmesi DEĞİL — mevcut sözleşmenin BOŞLUKLARI
 * ═══════════════════════════════════════════════════════════════════════════
 * `/campaigns`, `/campaigns/:id`, `/campaigns/:id/adsets`, `/adsets/:id/ads`,
 * `/campaigns/:id/creatives`, `/campaigns/status`, `/campaigns/budget` uçları
 * ZATEN CANLI ve girdi/çıktı tipleri `analytics.ts` içinde. Adım 4 bir backend
 * adımı değil, saf bir UI adımı: yazılacak olan o uçları TÜKETEN ekran.
 *
 * Buraya yalnız şunlar giriyor:
 *
 *   1. `analytics.ts`in söylediği ama TİPLEŞTİRMEDİĞİ kurallar. Sözleşme
 *      "arayüz bu kolona göre sıralamayı yalnız tek günlük aralıkta teklif
 *      etmeli" diyor ama bunu ölçebilecek bir fonksiyon vermiyor; üç ekran
 *      (tablo, kolon seçici, rapor) o cümleyi üç kez ayrı yorumlardı.
 *   2. Sunucuda VAR olup sözleşmede OLMAYAN tipler (`CampaignCreativeRow`).
 *   3. Sunucu ile arayüzün AYNI olmak zorunda olduğu sabitler
 *      (varsayılan kolon kümesi, seçim tavanı).
 *
 * Kampanya OLUŞTURMA sihirbazı burada DEĞİL → `./campaign-wizard.ts`.
 * Gerekçe `rmf.ts`in `analytics.ts`ten ayrılma gerekçesiyle aynı: okumanın
 * girdisi filtre ve dönem, oluşturmanın girdisi hedefleme ve faturalama
 * olayı; ikisinin ortak alanı yok.
 */

// ═══════════════════════════════════════════════════════════════════════════
// KD5 — kampanyada yayında olan kreatifler
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `GET /campaigns/:id/creatives` satırı.
 *
 * 🔴 BU TİP SUNUCUDA YEREL BİR `export interface` OLARAK DURUYORDU
 * (`apps/api/src/analytics/campaigns.service.ts`). Yani ucun yanıt şekli
 * derlenmiş bir pakette değil, API'nin içinde yaşıyordu ve `apps/web` onu
 * import EDEMİYORDU. Web tarafında ikinci bir kopya yazmak, iki tipin bir gün
 * ayrışması ve ayrışmanın derlemede GÖRÜNMEMESİ demekti — bu ekran satırları
 * `any` gibi okuyacaktı. Tip buraya taşındı; servis artık buradan import edip
 * kendi `export interface`ini SİLİYOR.
 *
 * ---------------------------------------------------------------------------
 * `thumbUrl` neden eklendi — `thumbnailKey` tarayıcıda AÇILMIYOR
 * ---------------------------------------------------------------------------
 * Uç bugün yalnız `thumbnailKey` döndürüyor: bucket'taki HAM nesne anahtarı.
 * Bucket public değil, yani o dizeyi `<img src>`e koymak boş kutu demek.
 * Kütüphane satırı (`CreativeRow`) aynı görsel için `thumbUrl` (imzalı adres)
 * taşıyor; bu uç taşımıyordu. Üç seçenekten (kreatif başına ayrı istek → N+1;
 * `Ad.previewUrl`e güvenmek → platform önizlemesi çoğu satırda null; ucu
 * genişletmek) sonuncusu seçildi: aynı ekranda 20 kreatif için 20 ek istek
 * atmak, çözülmüş bir problemi ikinci kez çözmek olurdu.
 *
 * `thumbnailKey` yine de KALIYOR — destek "hangi nesne" diye sorduğunda
 * cevaplayan tek alan o ve imzalı adres kısa ömürlü.
 *
 * ⚠️ `thumbUrl` `null` gelebilir: depolama yapılandırılmamışsa
 * (`CreativeStorageService.configured === false`) ya da kreatifin küçük
 * görseli hiç üretilmemişse. Arayüz bu durumda yer tutucu basar, kırık
 * görsel değil.
 */
export interface CampaignCreativeRow {
  adId: string;
  adName: string;
  /// ⚠️ `EntityStatus`in TAMAMI DEĞİL — bu uç yalnız bu beşini üretiyor.
  /// Rozet bileşeni yine dokuz değeri tanıyan `EntityStatusChip`, çünkü
  /// dar bir kümeyi dar bir bileşenle basmak ikinci bir rozet ailesi doğurur.
  adStatus: 'ACTIVE' | 'PAUSED' | 'DELETED' | 'ARCHIVED' | 'PENDING';
  /// Platformun kendi önizleme adresi. Çoğu satırda `null` — buna güvenilmez.
  previewUrl: string | null;
  headline: string | null;
  creativeId: string;
  creativeType: string;
  creativeStatus: string;
  creativeName: string | null;
  /// Ham S3 anahtarı. Tarayıcıda AÇILMAZ; teşhis içindir.
  thumbnailKey: string | null;
  /// İmzalı küçük görsel adresi — ekranın bastığı alan bu.
  thumbUrl: string | null;
  width: number | null;
  height: number | null;
  createdAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// KM8 — kolon kümesi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kolon seçilmediğinde gösterilen küme.
 *
 * 🔴 BU SABİT ARAYÜZDE DE GEREKLİ ve tek kopya olmak ZORUNDA. Değer bugüne
 * kadar `apps/api/src/analytics/metric-math.ts` içindeydi; API'ye özel bir
 * dosya, web onu import edemiyordu. İkinci bir kopya yazmak, kolon seçicinin
 * "varsayılan" dediği küme ile sunucunun gerçekten döndürdüğü kümenin
 * ayrışması demekti — kullanıcı hiç seçmediği bir kolonu görür ya da seçtiği
 * kolonu göremezdi. `metric-math.ts`teki `DEFAULT_COLUMNS` artık BURADAN
 * türetiliyor.
 *
 * Altı kolon: tabloyu on kolonla açmak mobilde okunmaz hâle getiriyor.
 */
export const DEFAULT_METRIC_COLUMNS: readonly MetricKey[] = [
  'spend',
  'impressions',
  'clicks',
  'ctr',
  'cpc',
  'roas',
] as const;

/// `hierarchyQuerySchema.columns` üst sınırı. Şemadaki `.max(10)` ile AYNI
/// sayı olmak zorunda: arayüz 11. kolonu seçtirirse istek 400 döner ve
/// kullanıcı sebebini hiçbir yerde okuyamaz.
export const MAX_TABLE_COLUMNS = 10;

/**
 * 🔴 GÖRÜNEN KOLONUN KAYNAĞI `totals` DEĞİL.
 *
 * `/keywords` ekranı `METRIC_KEYS.filter((k) => k in data.totals)` yazıyor ve
 * ORASI için doğru: `KeywordListResponse.totals` bir `Partial`. Ama
 * `HierarchyResponse.totals` tam `MetricTotals` arayüzü — 22 anahtarın HEPSİ
 * alan olarak mevcut. Aynı satır kampanya tablosunda 22 sütun üretir ve
 * mobilde ekranı yatay kaydırır.
 *
 * Görünen kolonun tek doğru kaynağı İSTEMCİNİN GÖNDERDİĞİ `columns`
 * parametresidir; `HierarchyRow.metrics` (gerçekten `Partial`) onun aynasıdır.
 * Bu fonksiyon o kararı tek yerde tutuyor.
 */
export function resolveTableColumns(selected: readonly MetricKey[] | undefined): MetricKey[] {
  if (!selected || selected.length === 0) return [...DEFAULT_METRIC_COLUMNS];
  return selected.slice(0, MAX_TABLE_COLUMNS);
}

// ═══════════════════════════════════════════════════════════════════════════
// KM2/KM8 — hangi metrik hangi KAPSAMDA anlamlı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bir metriğin gerçek değer üretebilmesi için kapsamın taşıması gereken şart.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden bu tablo var — sözleşme kuralı SÖYLÜYOR ama ÖLÇMÜYOR
 * ═══════════════════════════════════════════════════════════════════════════
 * `hierarchyQuerySchema.sort` yorumları üç ayrı uyarı taşıyor:
 *
 *   · `reach` / `frequency` çok günlü aralıkta HER SATIRDA `null`
 *     (`aggregatableOver: 'singleDay'`) ve sıralama `NULLS LAST` —
 *     tablo "hepsi boş" görünür.
 *   · Huni altılısı yalnız Meta'nın bildirdiği satırlarda dolu.
 *   · Gösterim payı beşlisi yalnız Google CAMPAIGN/ADSET satırlarında dolu.
 *
 * Üçü de "arayüz bunu teklif etmemeli / dipnot basmalı" diyor. O cümleyi üç
 * ekranın (tablo başlığı, kolon seçici, rapor oluşturucu) üç kez ayrı ayrı
 * yorumlaması, üç farklı davranış demekti. Karar burada bir kez veriliyor.
 *
 * 🔴 Bu tablo bir YASAK değil, bir TEKLİF kuralı. Kullanıcı adres çubuğuna
 * `sort=reach` yazarsa uç yine kabul eder ve doğru davranır (hepsi `null`).
 * Burada belirlenen şey neyin ÖNERİLECEĞİ ve neyin yanına dipnot düşüleceği.
 */
export interface MetricScopeRequirement {
  /// Yalnız tek günlük aralıkta gerçek değer üretir.
  singleDayOnly: boolean;
  /**
   * Yalnız bu platformlar bu metriği bildiriyor. Boş dizi = platform şartı yok.
   *
   * ⚠️ "Henüz yazılmadı" değil, "o platformda kavramın karşılığı yok" demek.
   * Dipnot metni bu yüzden "yakında" DİYEMEZ — `ConnectorCapabilities`
   * yorumundaki `canManageKeywords` ayrımıyla aynı ayrım.
   */
  reportedBy: readonly ('META' | 'GOOGLE' | 'TIKTOK' | 'LINKEDIN')[];
  /// Yalnız bu seviyelerde dolu. Boş dizi = seviye şartı yok.
  levels: readonly ('CAMPAIGN' | 'ADSET' | 'AD')[];
}

const NO_REQUIREMENT: MetricScopeRequirement = {
  singleDayOnly: false,
  reportedBy: [],
  levels: [],
};

const SINGLE_DAY: MetricScopeRequirement = {
  singleDayOnly: true,
  reportedBy: [],
  levels: [],
};

/// Huni — `FUNNEL_METRIC_KEYS` yorumu: bugün yalnız Meta bildiriyor.
const META_FUNNEL: MetricScopeRequirement = {
  singleDayOnly: false,
  reportedBy: ['META'],
  levels: [],
};

/// Gösterim payı — `IMPRESSION_SHARE_METRIC_KEYS` yorumu: yalnız Google
/// arama ağı ve yalnız kampanya/ad set seviyesinde. AD seviyesinde kolon
/// baştan boş gelir, teklif edilmesi anlamsız.
const GOOGLE_IMPRESSION_SHARE: MetricScopeRequirement = {
  singleDayOnly: false,
  reportedBy: ['GOOGLE'],
  levels: ['CAMPAIGN', 'ADSET'],
};

export const METRIC_SCOPE_REQUIREMENTS: Record<MetricKey, MetricScopeRequirement> = {
  spend: NO_REQUIREMENT,
  impressions: NO_REQUIREMENT,
  clicks: NO_REQUIREMENT,
  conversions: NO_REQUIREMENT,
  revenue: NO_REQUIREMENT,
  ctr: NO_REQUIREMENT,
  cpc: NO_REQUIREMENT,
  cpm: NO_REQUIREMENT,
  cpa: NO_REQUIREMENT,
  roas: NO_REQUIREMENT,
  reach: SINGLE_DAY,
  frequency: SINGLE_DAY,
  addToCart: META_FUNNEL,
  addToCartValue: META_FUNNEL,
  initiateCheckout: META_FUNNEL,
  initiateCheckoutValue: META_FUNNEL,
  purchases: META_FUNNEL,
  purchaseValue: META_FUNNEL,
  searchImpressionShare: GOOGLE_IMPRESSION_SHARE,
  searchTopImpressionShare: GOOGLE_IMPRESSION_SHARE,
  searchAbsoluteTopImpressionShare: GOOGLE_IMPRESSION_SHARE,
  searchBudgetLostImpressionShare: GOOGLE_IMPRESSION_SHARE,
  searchRankLostImpressionShare: GOOGLE_IMPRESSION_SHARE,
};

/// Ekranın o an baktığı kapsam. `platforms` boşsa "hepsi" demektir —
/// filtre uygulanmamış demek, kapsamda her platform VAR sayılır.
export interface MetricScopeContext {
  /// `from === to` ise tek günlük aralık.
  from: string;
  to: string;
  /// `MetricFilter.platforms`. `undefined` / boş = filtre yok.
  platforms?: readonly ('META' | 'GOOGLE' | 'TIKTOK' | 'LINKEDIN')[];
  level: 'CAMPAIGN' | 'ADSET' | 'AD';
}

/**
 * Bu metrik bu kapsamda gerçek değer üretebilir mi.
 *
 * `false` dönmesi metriği GİZLEMEK zorunda bırakmaz — kolon seçicide onu
 * "bu kapsamda boş kalır" notuyla göstermek de meşru bir karar. Zorunlu olan
 * tek şey: `false` iken kullanıcı sebebini ekranda okuyabilmeli.
 */
export function isMetricMeaningful(key: MetricKey, scope: MetricScopeContext): boolean {
  const req = METRIC_SCOPE_REQUIREMENTS[key];
  if (req.singleDayOnly && scope.from !== scope.to) return false;
  if (req.levels.length > 0 && !req.levels.includes(scope.level)) return false;
  if (req.reportedBy.length === 0) return true;
  // Platform filtresi yoksa kapsamda her platform var sayılır; metriği
  // bildiren en az bir platform kapsamda demektir.
  if (!scope.platforms || scope.platforms.length === 0) return true;
  return scope.platforms.some((p) => req.reportedBy.includes(p));
}

/**
 * Bu metriğe göre SIRALAMA teklif edilebilir mi.
 *
 * `isMetricMeaningful`den ayrı bir fonksiyon çünkü soru farklı: boş bir kolonu
 * göstermek (dipnotla) kabul edilebilir, ama HEPSİ `null` olan bir kolona göre
 * sıralamak tabloyu rastgele bir sıraya sokar ve kullanıcı sıraladığını sanır.
 * Bugün ikisi aynı kuralı kullanıyor; ayrı isimler, ayrıştıkları gün ikinci
 * bir çağrı yerini bulmak için.
 */
export function isSortOfferable(sort: HierarchyQuery['sort'], scope: MetricScopeContext): boolean {
  // `name`, `status`, `dailyBudget` metrik değil — varlığın kendi alanları,
  // her kapsamda dolu.
  if (sort === 'name' || sort === 'status' || sort === 'dailyBudget') return true;
  return isMetricMeaningful(sort, scope);
}

/**
 * Kapsam yüzünden boş kalacak seçili kolonlar — dipnot bunları sayar.
 *
 * Boş dizi dönmesi "dipnot basma" demektir. Sunucunun kendi kapsam bilgisi
 * (`AnalyticsSummary.funnel.missingPlatforms` ve `.impressionShare
 * .missingPlatforms`) bundan DAHA doğrudur: o gerçekten hangi satırın veri
 * bildirdiğini biliyor, bu yalnız filtreye bakıyor. Detay ekranında (özet
 * elde varken) sunucunun kapsamı kullanılır; hiyerarşi tablosunda özet yok,
 * bu fonksiyon kullanılır.
 */
export function emptyColumnsInScope(
  columns: readonly MetricKey[],
  scope: MetricScopeContext,
): MetricKey[] {
  return columns.filter((key) => !isMetricMeaningful(key, scope));
}

// ═══════════════════════════════════════════════════════════════════════════
// KM7 — toplu seçim
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 SEÇİM TAVANI İLE SAYFA BOYU ÇELİŞİYORDU.
 *
 * `entityStatusChangeSchema.targets` en çok 100 hedef alıyor,
 * `hierarchyQuerySchema.pageSize` ise 200'e kadar çıkıyor. "Sayfadaki hepsini
 * seç" 200 satırlık bir sayfada 100'ü aşar ve istek 400 döner — kullanıcı
 * hiçbir şey yapmadan hata alır.
 *
 * İki çözüm vardı: isteği parçalamak (her parçaya AYRI `Idempotency-Key`,
 * kısmi başarı raporlarını birleştirmek, biri patlarsa yarısı uygulanmış bir
 * toplu işlem) ya da SAYFA BOYUNU tavana kadar sınırlamak. İkincisi seçildi:
 * "sayfadaki hepsini seç" hiçbir zaman tavanı aşamaz ve toplu işlem TEK bir
 * çağrı, TEK bir anahtar, TEK bir kısmi başarı raporu olarak kalır.
 *
 * Sayfa boyu 200 isteyen bir kullanıcı yok; 100'ün üstünde satırı gözle tarayan
 * da yok. Kaybedilen şey teorik, kazanılan şey işlemin bölünmemesi.
 */
export const BULK_STATUS_MAX_TARGETS = 100;

/// KM10 — sayfa boyu seçenekleri. Tavanı `BULK_STATUS_MAX_TARGETS` belirliyor
/// (yukarıya bak), şemanın 200'ü değil.
export const CAMPAIGN_PAGE_SIZE_OPTIONS: readonly number[] = [25, 50, 100] as const;
export const CAMPAIGN_PAGE_SIZE_DEFAULT = 50;

/// Toplu yazma hedefi — `entityStatusChangeSchema.targets` elemanıyla aynı
/// şekil. Arayüz seçimi bu tipte tutar ve isteği ondan kurar.
export interface BulkTarget {
  level: 'CAMPAIGN' | 'ADSET' | 'AD';
  id: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Boş durum — DÖRT hâl, tek bayrak değil
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ekranın basacağı boş durum.
 *
 * `DataState` üçünü ayırıyor (bağlantı yok / senkron sürüyor / veri yok) ama
 * dördüncüsünü bilmiyor: FİLTRE eşleşmedi. Sunucu için ikisi de "satır yok";
 * kullanıcı için biri "hesap bağla", diğeri "filtreyi gevşet".
 *
 * 🔴 `no_data` DURUMUNUN DOĞRU CEVABI "HESAP BAĞLA" DEĞİL.
 * 22 Ağu 2026 gerçeği: iki bağlantı da HEALTHY, ikisinin de `lastSyncedAt`i
 * dolu, `campaigns` tablosu 0 satır. Yani senkron çalıştı ve hesapta gerçekten
 * kampanya yok. Bu ekranın açılışta göstereceği hâl `no_data` ve doğru çağrı
 * "ilk kampanyanı oluştur" (sihirbaza CTA) — "hesap bağla" demek, kullanıcıya
 * zaten yaptığı işi tekrar yaptırmak olurdu.
 */
export const CampaignEmptyKind = {
  /// Hiç bağlı reklam hesabı yok → bağlantı ekranına.
  NO_CONNECTION: 'no_connection',
  /// İlk senkron sürüyor → tablo kilitlenmez, şerit basılır.
  SYNCING: 'syncing',
  /// Bağlantı var, senkron bitti, kampanya yok → SİHİRBAZA çağrı.
  NO_CAMPAIGNS: 'no_campaigns',
  /// Kampanya var ama filtre/arama eşleşmedi → filtreyi temizle.
  NO_RESULTS: 'no_results',
} as const;
export type CampaignEmptyKind = (typeof CampaignEmptyKind)[keyof typeof CampaignEmptyKind];

/**
 * `hasActiveFilters` = kullanıcı arama, durum, platform, hesap ya da minSpend
 * uygulamış mı. Tarih aralığı BUNA DAHİL DEĞİL: aralık her zaman dolu
 * (zorunlu alan) ve "filtreyi temizle" demek onu temizlemek anlamına gelmez.
 */
export function campaignEmptyStateKind(
  state: DataState,
  hasActiveFilters: boolean,
): CampaignEmptyKind {
  if (state === DataState.NO_CONNECTION) return CampaignEmptyKind.NO_CONNECTION;
  if (state === DataState.SYNCING) return CampaignEmptyKind.SYNCING;
  return hasActiveFilters ? CampaignEmptyKind.NO_RESULTS : CampaignEmptyKind.NO_CAMPAIGNS;
}

// ═══════════════════════════════════════════════════════════════════════════
// Karışık para birimi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Toplam satırının para birimi — karışıksa `null`.
 *
 * 🔴 BU YOL BUGÜN TETİKLENİYOR. Demo çalışma alanında iki hesap iki para
 * biriminde: Meta "By Nini" TRY, X "Mentorivo" EUR. Hesap filtresi
 * uygulanmadığında `HierarchyResponse.mixedCurrency` `true`, `currency` `null`
 * gelir. Satırlar kendi `currency`sini taşıdığı için hücreler doğru; ama
 * `tfoot` toplamı 1.000 TRY ile 40 EUR'un toplamı olur ve o sayı hiçbir şey
 * anlatmaz. `null` dönmesi arayüze "para birimi işareti BASMA ve dipnot düş"
 * demektir.
 */
export function resolveTotalsCurrency(
  currency: string | null,
  mixedCurrency: boolean,
): string | null {
  return mixedCurrency ? null : currency;
}

// ═══════════════════════════════════════════════════════════════════════════
// KD3 — zaman serisi varsayılanı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Detay ekranı grafiğinin açılış metrikleri.
 *
 * `timeseriesQuerySchema.metrics` en az 1, en çok 4 istiyor. İkisi seçildi ve
 * ikisi de `aggregatableOver: 'any'`: grafiğin ilk hâli hiçbir kapsamda boş
 * açılmamalı. `reach` gibi tek günlük bir metriği varsayılan yapmak, 30 günlük
 * varsayılan aralıkta düz bir boş grafik demekti.
 */
export const CAMPAIGN_DETAIL_TIMESERIES_DEFAULT: readonly MetricKey[] = [
  'spend',
  'clicks',
] as const;
export const MAX_TIMESERIES_METRICS = 4;

// ═══════════════════════════════════════════════════════════════════════════
// Adres durumu (KM3/KM4/KM10 — liste ekranı)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kampanya listesinin ADRESTE tutulan durumu.
 *
 * `hierarchyQuerySchema`nın kendisi kullanılamıyor çünkü o SUNUCU sözleşmesi:
 * `from`/`to` zorunlu ve varsayılansız, `csvParam` diziye çeviriyor. Ekranın
 * adres durumu ise eksik olabilir (kullanıcı henüz aralık seçmedi, sunucu
 * bileşeni varsayılanı üretiyor) ve `readQuery` ham dize veriyor.
 *
 * Beyaz liste olmasının sebebi `/keywords` ekranıyla aynı: tanınmayan bir
 * parametreyi sunucuya iletmek, bir gün başka bir ekranın parametresinin
 * sessizce bu ucu filtrelemesi demek.
 */
export const campaignListStateSchema = z.object({
  /// `YYYY-MM-DD`. Boşsa sunucu bileşeni son 30 günü üretir (bugün DAHİL).
  from: z.string().optional(),
  to: z.string().optional(),
  q: z.string().optional(),
  platforms: z.string().optional(),
  adAccountIds: z.string().optional(),
  status: z.string().optional(),
  minSpend: z.string().optional(),
  sort: z.string().optional(),
  dir: z.enum(['asc', 'desc']).optional(),
  page: z.string().optional(),
  pageSize: z.string().optional(),
  /// Virgülle ayrılmış `MetricKey` listesi (KM8).
  columns: z.string().optional(),
  /// Açık olan kampanya satırlarının kimlikleri — katlama durumu da adreste.
  /// Sayfa yenilendiğinde ya da bağlantı paylaşıldığında aynı satırlar açık
  /// gelir; tembel yükleme bunu ucuz kılıyor (yalnız açık olanlar çekilir).
  open: z.string().optional(),
});
export type CampaignListState = z.infer<typeof campaignListStateSchema>;

/// Varsayılan dönem uzunluğu (gün). Bugün DAHİL — `/keywords` ekranındaki
/// `resolveRange()` ile AYNI aritmetik: `from = bugün - (gün - 1)`.
export const CAMPAIGN_DEFAULT_RANGE_DAYS = 30;

/// KM4 dönem şeridinin sunduğu uzunluklar. `MAX_RANGE_DAYS` (400) altında
/// kalıyor; 400'ü aşan bir seçenek 400 `RANGE_TOO_LARGE` demek olurdu.
export const CAMPAIGN_RANGE_PRESETS: readonly number[] = [1, 7, 14, 30, 90] as const;

/**
 * Tek günlük seçenek listede BİLEREK var (`1`).
 *
 * `reach` ve `frequency` yalnız orada gerçek değer üretiyor
 * (`METRIC_SCOPE_REQUIREMENTS`). Tek günlük aralık sunulmasaydı bu iki metrik
 * arayüzden hiç erişilemez olurdu — sözleşmede var, ekranda yok.
 */
export const SINGLE_DAY_PRESET = 1;

// ═══════════════════════════════════════════════════════════════════════════
// KM9 — dışa aktarma
// ═══════════════════════════════════════════════════════════════════════════

/**
 * CSV isteği kuyruğa düştüyse yoklama aralığı (ms).
 *
 * 🔴 YOKLAMA SONSUZA DÖNMEZ: `MAX_EXPORT_POLL_ATTEMPTS` sonunda arayüz
 * durur ve "hazır olduğunda bildirim gelecek" der. Sınırsız yoklama, 15
 * dakikalık access çerezi ölünce 401'e düşüp sessizce sonsuza dönen bir
 * döngüye çevriliyor — bu tuzak bu projede bir kez yaşandı.
 */
export const EXPORT_POLL_INTERVAL_MS = 2_000;
export const MAX_EXPORT_POLL_ATTEMPTS = 45;

/// `exportSchema.columns` da en çok 10 — tablo kolonlarıyla aynı tavan.
/// Ayrı bir sabit yazılmadı: ikisi ayrışırsa kullanıcı ekranda gördüğü
/// kolonu CSV'de bulamaz.
export const MAX_EXPORT_COLUMNS = MAX_TABLE_COLUMNS;

// ═══════════════════════════════════════════════════════════════════════════
// Doğrulama — sözlük ile bu dosya ayrışmasın
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `METRIC_SCOPE_REQUIREMENTS` tüm `METRIC_KEYS`i kapsıyor mu.
 *
 * `Record<MetricKey, …>` zaten derlemede zorluyor; bu satır ÇALIŞMA ZAMANINDA
 * da doğruluyor çünkü sözlük büyüdüğünde (yeni bir metrik eklendiğinde) bu
 * dosyanın da büyümesi gerektiğini derleyici söyler ama testin kırılması daha
 * erken söyler. Kural: sözleşme listelerini elle KOPYALAMA, OKU.
 */
export const METRIC_SCOPE_COVERAGE_OK: boolean = METRIC_KEYS.every(
  (key) => METRIC_SCOPE_REQUIREMENTS[key] !== undefined,
);

/// Varsayılan kolonların hepsi geçerli metrik mi (yazım hatası kalkanı).
export const DEFAULT_COLUMNS_VALID: boolean = DEFAULT_METRIC_COLUMNS.every(
  (key) => metricKeySchema.safeParse(key).success,
);
